import {
  IPlatformService,
  Platform,
  TVKeyMap,
  DeviceInfo,
  ScreenOrientation,
  OrientationChangeCallback,
} from '../types';
import { WEBOS_KEYS } from '../keys';
import { generateUUID } from '../../../utils/uuid';

declare global {
  interface Window {
    webOS?: {
      platformBack: () => void;
      deviceInfo: (callback: (info: Record<string, unknown>) => void) => void;
      service?: {
        request(
          uri: string,
          params: {
            method: string;
            parameters: Record<string, unknown>;
            // Payload do callback do webOS varia muito por serviço — caller
            // narrow específica (e.g., { volume: number }). Unknown aqui evita
            // any sem perder type-safety na chamada (caller pode tipar pela
            // assinatura específica que ele passa).
            onSuccess?: (result: Record<string, unknown>) => void;
            onFailure?: (result: Record<string, unknown>) => void;
            onComplete?: (result: Record<string, unknown>) => void;
          },
        ): void;
      };
    };
    PalmSystem?: {
      stageReady: () => void;
    };
  }
}

export class WebOSPlatformService implements IPlatformService {
  readonly platform: Platform = 'webos';
  readonly keys: TVKeyMap = WEBOS_KEYS;
  private isInitialized = false;
  private orientationCallbacks: Set<OrientationChangeCallback> = new Set();

  init(): void {
    if (this.isInitialized) return;

    console.log('[WebOSPlatformService] Initializing...');
    this.isInitialized = true;

    if (this.isNativeEnvironment()) {
      console.log('[WebOSPlatformService] Running on LG webOS TV');
      // Signal to webOS that app is ready
      if (window.PalmSystem?.stageReady) {
        window.PalmSystem.stageReady();
      }
    } else {
      console.log('[WebOSPlatformService] webOS APIs not available (dev mode)');
    }
  }

  exit(): void {
    if (this.isNativeEnvironment()) {
      try {
        // webOS uses platformBack to exit or go back
        window.webOS?.platformBack();
      } catch (error) {
        console.error('[WebOSPlatformService] Failed to exit:', error);
      }
    } else {
      console.log(
        '[WebOSPlatformService] Exit requested (dev mode - no action)',
      );
    }
  }

  /**
   * Restart remoto (PLAYER_RESTART_PLAN).
   * 'refresh' = reload do WebView. 'reboot_device' não tem API em webOS sem
   * privilégio OEM.
   */
  async restart(mode: 'refresh' | 'reboot_device'): Promise<void> {
    if (mode === 'reboot_device') {
      throw new Error('unsupported: reboot_device não disponível em webOS');
    }
    console.log(
      '[WebOSPlatformService] Restart refresh — recarregando WebView',
    );
    window.location.reload();
  }

  onHide(): void {
    console.log('[WebOSPlatformService] Application hidden');
  }

  onResume(): void {
    console.log('[WebOSPlatformService] Application resumed');
  }

  isNativeEnvironment(): boolean {
    return (
      typeof window !== 'undefined' && (!!window.webOS || !!window.PalmSystem)
    );
  }

  canExit(): boolean {
    return true;
  }

  hasColorButtons(): boolean {
    return true;
  }

  hasMediaKeys(): boolean {
    return true;
  }

  /** TV de verdade: controle remoto, sem toque, 1 decoder de vídeo por hardware. */
  isTV(): boolean {
    return true;
  }

  /**
   * Returns the LG webOS device ID.
   * Uses the webOS deviceInfo API to get a unique identifier.
   */
  async getHardwareId(): Promise<string> {
    if (!this.isNativeEnvironment()) {
      // In dev mode without webOS APIs, generate a persistent dev ID
      let devId = localStorage.getItem('dev_webos_hardware_id');
      if (!devId) {
        devId = `DEV-WEBOS-${generateUUID().substring(0, 8).toUpperCase()}`;
        localStorage.setItem('dev_webos_hardware_id', devId);
      }
      console.log(
        '[WebOSPlatformService] Dev mode - using generated ID:',
        devId,
      );
      return devId;
    }

    return new Promise((resolve, reject) => {
      try {
        if (window.webOS?.deviceInfo) {
          window.webOS.deviceInfo((info) => {
            // webOS provides 'deviceId' or 'serialNumber' in device info
            const deviceId = (info.deviceId ||
              info.serialNumber ||
              info.udid) as string;
            if (deviceId) {
              const hardwareId = `WEBOS-${deviceId}`;
              console.log(
                '[WebOSPlatformService] Hardware ID obtained:',
                hardwareId.substring(0, 20) + '...',
              );
              resolve(hardwareId);
            } else {
              reject(new Error('No device ID found in webOS deviceInfo'));
            }
          });
        } else {
          reject(new Error('webOS deviceInfo API not available'));
        }
      } catch (error) {
        console.error(
          '[WebOSPlatformService] Failed to get hardware ID:',
          error,
        );
        reject(new Error('Hardware ID not available on this webOS device'));
      }
    });
  }

  /**
   * Collects device information from LG webOS APIs.
   */
  /**
   * N/A — a webOS não expõe memória do sistema ao app (diferente da Tizen, que
   * tem `tizen.systeminfo`).
   */
  async getSystemMemoryMb(): Promise<{
    freeMb: number;
    totalMb: number | null;
  } | null> {
    return null;
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    const baseInfo: DeviceInfo = {
      platform: 'webos',
      manufacturer: 'LG',
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      orientation: this.getOrientation(),
    };

    if (!this.isNativeEnvironment()) {
      console.log(
        '[WebOSPlatformService] Dev mode - returning basic device info',
      );
      baseInfo.model = 'Development Mode';
      baseInfo.platformVersion = 'N/A';
      return baseInfo;
    }

    return new Promise((resolve) => {
      try {
        if (window.webOS?.deviceInfo) {
          window.webOS.deviceInfo((info) => {
            baseInfo.model =
              ((info.modelName || info.model) as string) || 'Unknown';
            baseInfo.platformVersion =
              ((info.sdkVersion || info.version) as string) || 'Unknown';
            baseInfo.extra = {
              ...info,
            };
            console.log(
              '[WebOSPlatformService] Device info collected:',
              baseInfo,
            );
            resolve(baseInfo);
          });
        } else {
          console.log('[WebOSPlatformService] deviceInfo API not available');
          resolve(baseInfo);
        }
      } catch (error) {
        console.error(
          '[WebOSPlatformService] Error collecting device info:',
          error,
        );
        resolve(baseInfo);
      }
    });
  }

  // Orientation methods

  /**
   * TVs LG webOS não suportam mudança de orientação em runtime.
   */
  supportsOrientationChange(): boolean {
    return false;
  }

  /**
   * Detecta orientação atual baseado nas dimensões da tela.
   */
  getOrientation(): ScreenOrientation {
    return screen.width > screen.height ? 'landscape' : 'portrait';
  }

  /**
   * Registra callback para mudanças de orientação.
   * Em TVs fixas, o callback nunca será chamado, mas mantemos a interface.
   */
  onOrientationChange(callback: OrientationChangeCallback): () => void {
    this.orientationCallbacks.add(callback);
    return () => this.orientationCallbacks.delete(callback);
  }

  /**
   * Reads physical backlight level on LG webOS via Luna service.
   *
   * <p>API: {@code luna://com.webos.settingsservice/getSystemSettings}
   * with {@code keys: ['backlight']}. Requires the {@code webos.service}
   * permission in {@code appinfo.json}.
   *
   * <p>Returns null when API unavailable or call fails.
   */
  async getHardwareBrightness(): Promise<number | null> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      const webOS = w?.webOS;
      const service = webOS?.service;
      if (!service || typeof service.request !== 'function') return null;

      return await new Promise<number | null>((resolve) => {
        try {
          service.request('luna://com.webos.settingsservice', {
            method: 'getSystemSettings',
            parameters: { category: 'picture', keys: ['backlight'] },
            onSuccess: (response: { settings?: { backlight?: number } }) => {
              const v = response?.settings?.backlight;
              if (typeof v === 'number' && v >= 0 && v <= 100) {
                resolve(Math.round(v));
              } else {
                resolve(null);
              }
            },
            onFailure: () => resolve(null),
          });
          // Timeout defensivo — luna pode ficar pendente
          setTimeout(() => resolve(null), 2000);
        } catch {
          resolve(null);
        }
      });
    } catch (e) {
      console.warn('[WebOSPlatformService] getHardwareBrightness failed:', e);
      return null;
    }
  }

  /**
   * webOS apps geralmente não têm permissão pra alterar backlight do TV.
   * Throws 'unsupported' — chamador deve cair no overlay CSS.
   */
  async setHardwareBrightness(_level: number): Promise<void> {
    const err = new Error(
      'Hardware brightness unsupported on webOS (TV settings only)',
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (err as any).code = 'unsupported';
    throw err;
  }

  // OTA Update methods

  /**
   * Install an OTA update bundle using webOS Luna API.
   * @param bundlePath Local file path to the downloaded bundle (.ipk file)
   * @returns Promise that resolves when installation is complete
   * @throws Error if installation fails or platform API is not available
   */
  async installUpdate(bundlePath: string): Promise<void> {
    console.log('[WebOSPlatformService] Installing update:', bundlePath);

    if (!this.isNativeEnvironment()) {
      console.warn(
        '[WebOSPlatformService] Cannot install update in dev mode (webOS API not available)',
      );
      throw new Error('OTA installation is only supported on webOS devices');
    }

    if (!window.webOS?.service) {
      console.error('[WebOSPlatformService] webOS service API not available');
      throw new Error('webOS service API is not available on this device');
    }

    return new Promise<void>((resolve, reject) => {
      try {
        const service = window.webOS!.service!;

        // Use Luna service for package installation
        // luna://com.webos.appInstallService/install
        service.request('luna://com.webos.appInstallService/dev', {
          method: 'install',
          parameters: {
            id: 'com.efflux.player', // App ID
            ipkUrl: bundlePath,
            subscribe: true,
          },
          onSuccess: (result: Record<string, unknown>) => {
            console.log('[WebOSPlatformService] Installation result:', result);
            if (result.returnValue as boolean | undefined) {
              // Check installation status
              if ((result.status as string | undefined) === 'installed') {
                console.log(
                  '[WebOSPlatformService] Installation completed successfully',
                );
                resolve();
              } else if (result.statusValue) {
                console.log(
                  `[WebOSPlatformService] Installation progress: ${result.statusValue as string | undefined}%`,
                );
              }
            } else {
              console.error(
                '[WebOSPlatformService] Installation failed:',
                result.errorText,
              );
              reject(
                new Error(
                  (result.errorText as string | undefined) ||
                    'Failed to install update on webOS',
                ),
              );
            }
          },
          onFailure: (result: Record<string, unknown>) => {
            console.error('[WebOSPlatformService] Installation error:', result);
            reject(
              new Error(
                (result.errorText as string | undefined) ||
                  (result.errorCode as string | undefined) ||
                  'Installation failed on webOS',
              ),
            );
          },
          onComplete: (result: Record<string, unknown>) => {
            console.log(
              '[WebOSPlatformService] Installation completed:',
              result,
            );
          },
        });
      } catch (error) {
        console.error(
          '[WebOSPlatformService] Error during installation:',
          error,
        );
        reject(
          new Error(
            `Installation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          ),
        );
      }
    });
  }

  /**
   * Restart the application after update.
   * On webOS, this uses platformBack or reloads the app.
   */
  restartApp(): void {
    console.log('[WebOSPlatformService] Restarting app after update...');

    if (!this.isNativeEnvironment()) {
      console.warn(
        '[WebOSPlatformService] Dev mode - simulating restart (reload)',
      );
      window.location.reload();
      return;
    }

    try {
      // Use platformBack to exit, or reload the page
      if (window.webOS?.platformBack) {
        window.webOS.platformBack();
      } else {
        // Fallback to reload
        window.location.reload();
      }
    } catch (error) {
      console.error('[WebOSPlatformService] Failed to restart app:', error);
      // Fallback to reload
      window.location.reload();
    }
  }

  /**
   * Get temporary directory for OTA downloads.
   * On webOS, this returns the media/internal path.
   * @returns Path to temp directory
   */
  getTempDirectory(): string {
    if (!this.isNativeEnvironment()) {
      console.log('[WebOSPlatformService] Dev mode - using browser temp path');
      return '/tmp';
    }

    // webOS commonly uses /media/internal for app storage
    return '/media/internal';
  }

  // Fullscreen methods (no-op for TVs - always fullscreen)

  /**
   * webOS TVs don't support fullscreen toggle - they always run fullscreen.
   */
  supportsFullscreen(): boolean {
    return false;
  }

  /**
   * webOS TVs are always in fullscreen mode.
   */
  isFullscreen(): boolean {
    return true;
  }

  /**
   * No-op on webOS TVs - already fullscreen.
   */
  async enterFullscreen(): Promise<void> {
    // No-op - webOS apps always run fullscreen
  }

  /**
   * No-op on webOS TVs - cannot exit fullscreen.
   */
  async exitFullscreen(): Promise<void> {
    // No-op - webOS apps always run fullscreen
  }

  /**
   * No-op on webOS TVs - fullscreen state never changes.
   * Returns a cleanup function that does nothing.
   */
  onFullscreenChange(): () => void {
    // No-op - webOS apps are always fullscreen
    return () => {};
  }

  // Screen Wake Lock (V058) — no-op: a TV webOS mantém a tela acesa
  // nativamente (signage 24/7). A flag keepScreenAwake é de browser.
  supportsWakeLock(): boolean {
    return false;
  }

  async requestScreenWakeLock(): Promise<void> {
    // no-op (tela acesa nativa)
  }

  async releaseScreenWakeLock(): Promise<void> {
    // no-op
  }
}
